import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom-v5-compat";
import {
  HotelsAvailabilityActions,
  HotelsAvailabilitySelectors,
} from "@b2bportal/core-hotels";
import type { PackagesAutocompleteResult } from "@b2bportal/core-packages";
import { PackageType } from "@b2bportal/core-packages";
import { CallState, type AppDispatch } from "@b2bportal/core-types";
import type { WithOptionalProps } from "@b2bportal/core-types";
import type { DateRange } from "@b2bportal/lodging-api";
import type { Travelers } from "@b2bportal/packages-api";
import { DateTimeFormatStyle, useI18nContext } from "@hopper-b2b/i18n";
import { createQueryParams } from "@overrides/utilities";
import { PACKAGES_QUERY_PARAM } from "@tenant/packages";
import type { PackageSearchQueryParams } from "@tenant/types";
import dayjs from "dayjs";
import {
  MINIMUM_DRIVER_AGE,
  DEFAULT_DRIVER_AGE,
} from "../PackageSearch/constants";
import { isSameRequestedSearch } from "./isSameRequestedSearch";
import { useValidatePackageRequiredHotelShopQueryParams } from "@/pages/Packages/pages/PackageHotelShopPage/hooks/useValidatePackageRequiredHotelShopQueryParams";
import { PackageSearchSelectors } from "@/store/packages/selectors";
import { PackagesSearchActions } from "@/store/packages/slice/packagesSearch";
import { PACKAGE_UP_SELL_PARAMS } from "@/tracking/upSell/packageUpSellParams";

/**
 * Custom hook for hotel availability search in the packages flow.
 * Overrides the default hotel search handlers to use the packages requisition endpoint.
 */
export const usePackageHotelAvailabilitySearch = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t, formatDateTime } = useI18nContext();
  const [currentSearchParams] = useSearchParams();

  // Blocks auto-search while initializePackages is in flight: at that point
  // the Hotels store dateRange still reflects the previous requisition, so
  // calling updateUrlParams with it would overwrite the correct URL dates.
  const initializedState = useSelector(
    PackageSearchSelectors.getInitializedState
  );

  // The URL is the source of truth until requestedSearch catches up to it.
  // On re-entry (e.g. back to home, then a new search) this hook mounts with
  // origin/destination/dateRange left over from the previous visit, and its
  // effect runs before usePackageHotelAvailabilityPageFetch's (child effects
  // run first), so the InProcess guard isn't set yet. Writing those stale
  // values back to the URL ping-pongs initializePackages between the old and
  // new search forever. Skip until the requested search matches the URL.
  // Failed is let through so the form still works after a failed init.
  const urlSearch = useValidatePackageRequiredHotelShopQueryParams();
  const requestedSearch = useSelector(
    PackageSearchSelectors.getRequestedSearch
  );
  const isUrlAheadOfRequestedSearch =
    requestedSearch != null &&
    !isSameRequestedSearch(urlSearch, requestedSearch);

  const dateRange = useSelector(
    HotelsAvailabilitySelectors.getSearchFormDateRange
  );

  const origin = useSelector(PackageSearchSelectors.getSearchFormOrigin);
  const destination = useSelector(
    PackageSearchSelectors.getSearchFormDestination
  );
  const driverAge = useSelector(PackageSearchSelectors.getSearchFormDriverAge);
  const packageType = useSelector(
    PackageSearchSelectors.getSearchFormPackageType
  );
  const packageGuests = useSelector(PackageSearchSelectors.getSearchFormGuests);
  const packageTypeIncludesFlights = packageType !== PackageType.HOTEL_CAR;

  const handleGuestsChange = useCallback(
    (newGuests: Travelers) => {
      dispatch(PackagesSearchActions.setNewGuests(newGuests));
      // Keep the hotel availability store in sync so date-range selectors
      // and other hotel-specific hooks see up-to-date guest counts.
      dispatch(
        HotelsAvailabilityActions.setNewGuests({
          adults: newGuests.adultCount,
          children: newGuests.childAges,
        })
      );
    },
    [dispatch]
  );

  const [formDateRange, setFormDateRange] = useState<
    WithOptionalProps<DateRange, "from" | "until">
  >({});

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Track the last search to prevent duplicate calls
  const lastSearchRef = useRef<string>("");

  // Mirror the committed Hotels dateRange into the picker whenever it changes,
  // not just on mount. On re-entry with a new search the store still holds the
  // previous visit's dates at mount; seeding only once left the picker showing
  // those while results were for the new search, so picking the loaded dates
  // looked like "no change" in handleDateRangeClose and never re-shopped.
  // In-progress picks are safe: they only touch local state until close.
  useEffect(() => {
    setFormDateRange({ from: dateRange.from, until: dateRange.until });
  }, [dateRange.from, dateRange.until]);

  /**
   * Helper to update URL query parameters with new search values.
   * This triggers usePackageHotelAvailabilityPageFetch to call initializePackages.
   * Includes deduplication to prevent multiple rapid updates from triggering duplicate calls.
   */
  const updateUrlParams = (searchParams: PackageSearchQueryParams) => {
    const searchKey = JSON.stringify(searchParams);

    // Prevent duplicate searches - centralized check
    if (searchKey === lastSearchRef.current) {
      return;
    }

    lastSearchRef.current = searchKey;

    const queryParams = createQueryParams({
      [PACKAGES_QUERY_PARAM.destination]: searchParams.destination,
      [PACKAGES_QUERY_PARAM.origin]: searchParams.origin,
      [PACKAGES_QUERY_PARAM.fromDate]: dayjs(searchParams.fromDate).toDate(),
      [PACKAGES_QUERY_PARAM.untilDate]: dayjs(searchParams.untilDate).toDate(),
      [PACKAGES_QUERY_PARAM.adults]: searchParams.adults,
      [PACKAGES_QUERY_PARAM.children]: searchParams.children,
      [PACKAGES_QUERY_PARAM.packageType]: searchParams.packageType,
      [PACKAGES_QUERY_PARAM.driverAge]: searchParams.driverAge,
      [PACKAGES_QUERY_PARAM.roomsCount]: searchParams.roomCount,
    });
    if (searchParams.infantsOnLap) {
      queryParams.set(
        PACKAGES_QUERY_PARAM.infantsOnLap,
        String(searchParams.infantsOnLap)
      );
    }
    if (searchParams.infantsInSeat) {
      queryParams.set(
        PACKAGES_QUERY_PARAM.infantsInSeat,
        String(searchParams.infantsInSeat)
      );
    }
    // Preserve the up-sell params so the banner stays visible and the booking
    // stays attributed after updateUrlParams replaces the URL on mount.
    PACKAGE_UP_SELL_PARAMS.forEach((param) => {
      const value = currentSearchParams.get(param);
      if (value) queryParams.set(param, value);
    });
    navigate({ search: queryParams.toString() }, { replace: true });
  };

  const handleDateRangeFromChange = (date: string | undefined) => {
    setFormDateRange({ from: date, until: undefined });
    dispatch(PackagesSearchActions.setNewDateFrom(date));
  };

  const handleDateRangeUntilChange = (date: string | undefined) => {
    setFormDateRange((prev) => ({ ...prev, until: date }));
    dispatch(PackagesSearchActions.setNewDateUntil(date));
  };

  const handlePlaceChange = (
    destination: PackagesAutocompleteResult | null
  ) => {
    if (destination != null) {
      dispatch(PackagesSearchActions.setNewDestination(destination));
      // For HOTEL_CAR, origin and destination should always be the same
      if (packageType === PackageType.HOTEL_CAR) {
        dispatch(PackagesSearchActions.setNewOrigin(destination));
      }
    }
  };

  const handleDateRangeClose = () => {
    const { from, until } = formDateRange;

    // Only proceed if we have a complete date range
    if (from != null && until != null && dateRange) {
      const hasChanged =
        !dayjs(from).isSame(dateRange.from, "date") ||
        !dayjs(until).isSame(dateRange.until, "date");

      if (hasChanged) {
        // Update both stores for consistency
        dispatch(HotelsAvailabilityActions.setNewDateRange({ from, until }));
        dispatch(PackagesSearchActions.setNewDateRange({ from, until }));
      }
    }
  };

  const handleOriginChange = (newOrigin: PackagesAutocompleteResult | null) => {
    if (newOrigin != null) {
      dispatch(PackagesSearchActions.setNewOrigin(newOrigin));
      // For HOTEL_CAR, origin and destination should always be the same
      if (packageType === PackageType.HOTEL_CAR) {
        dispatch(PackagesSearchActions.setNewDestination(newOrigin));
      }
    }
  };

  const handleDriverAgeChange = (driverAgeValue: string | undefined) => {
    if (driverAgeValue == null || driverAgeValue === "") {
      dispatch(PackagesSearchActions.setDriverAge(undefined));
    } else {
      const age = Number(driverAgeValue);
      // Only accept valid integers that meet minimum driver age requirement
      // Invalid ages are silently ignored to preserve previous valid value
      if (!isNaN(age) && Number.isInteger(age) && age >= 0 && age <= 120) {
        dispatch(PackagesSearchActions.setDriverAge(age));
      }
    }
  };

  // Determine if driver age field should be shown
  // All PackageType's include cars except for HOTEL_FLIGHT
  const shouldShowDriverAge = useMemo(() => {
    return packageType !== PackageType.HOTEL_FLIGHT;
  }, [packageType]);

  const isDriverAgeValid = useMemo(() => {
    if (!shouldShowDriverAge) return true;
    return driverAge != null && driverAge >= MINIMUM_DRIVER_AGE;
  }, [shouldShowDriverAge, driverAge]);

  // For HOTEL_CAR, we don't need origin field since origin = destination
  const shouldShowOrigin = useMemo(() => {
    return packageType !== PackageType.HOTEL_CAR;
  }, [packageType]);

  // Auto-search whenever committed form state is complete and valid.
  // Uses `dateRange` (Hotels store) rather than `formDateRange` (local state)
  // so the effect only fires after the DateRangePicker closes with both dates set.
  // Mobile dialog defers search until it closes.
  useEffect(() => {
    if (isDialogOpen) return;
    if (initializedState === CallState.InProcess) return;
    if (isUrlAheadOfRequestedSearch && initializedState !== CallState.Failed) {
      return;
    }

    const effectiveOrigin =
      packageType === PackageType.HOTEL_CAR && origin == null
        ? destination
        : origin;

    if (
      effectiveOrigin == null ||
      destination == null ||
      dateRange.from == null ||
      dateRange.until == null ||
      !isDriverAgeValid
    ) {
      return;
    }

    // Pass raw labels — URLSearchParams handles percent-encoding.
    // Pre-encoding with encodeURIComponent causes double-encoding, which shifts
    // searchKey on every call and allows redundant initializePackages dispatches.
    const searchParams: PackageSearchQueryParams = {
      origin: effectiveOrigin.label,
      destination: destination.label,
      fromDate: dayjs(dateRange.from).format("YYYY-MM-DD"),
      untilDate: dayjs(dateRange.until).format("YYYY-MM-DD"),
      adults: packageGuests.adultCount,
      children:
        packageGuests.childAges.length > 0 ? packageGuests.childAges : null,
      infantsOnLap: packageGuests.infantOnLapAges.length || undefined,
      infantsInSeat: packageGuests.infantInSeatAges.length || undefined,
      roomCount: 1,
      packageType: packageType,
      driverAge:
        driverAge != null && driverAge >= MINIMUM_DRIVER_AGE
          ? driverAge
          : DEFAULT_DRIVER_AGE,
    };

    updateUrlParams(searchParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDialogOpen,
    initializedState,
    isUrlAheadOfRequestedSearch,
    origin,
    destination,
    dateRange,
    packageGuests,
    driverAge,
    packageType,
    isDriverAgeValid,
  ]);

  const totalTravellers =
    packageGuests.adultCount +
    packageGuests.childAges.length +
    packageGuests.infantOnLapAges.length +
    packageGuests.infantInSeatAges.length;

  return {
    context: {
      dateRange: formDateRange,
      packageGuests,
      packageTypeIncludesFlights,
      destination,
      origin,
      driverAge,
      shouldShowDriverAge,
      shouldShowOrigin,
      isDriverAgeValid,
    },
    handlers: {
      handleDateRangeFromChange,
      handleDateRangeUntilChange,
      handlePlaceChange,
      handleGuestsChange,
      handleDateRangeClose,
      handleOriginChange,
      handleDriverAgeChange,
      onDialogOpen: () => setIsDialogOpen(true),
      onDialogClose: () => setIsDialogOpen(false),
    },
    text: {
      locationLabel: t("core-hotels.search.location.label"),
      locationData: destination?.label,
      searchSummary:
        dateRange.from && dateRange.until
          ? t("core-hotels.search.summary", {
              fromDate: formatDateTime(
                dayjs(dateRange.from).toDate(),
                DateTimeFormatStyle.ShortMonthDayShortWeekday
              ),
              untilDate: formatDateTime(
                dayjs(dateRange.until).toDate(),
                DateTimeFormatStyle.ShortMonthDayShortWeekday
              ),
              count: totalTravellers,
            })
          : t("core-hotels.search.select-dates"),
      search: t("core-hotels.search.button"),
      title: t("core-packages.hotels.title"),
      originLabel: t("core-packages.search.origin"),
      driverAgeLabel: t("core-cars.search.driverAge"),
      driverAgePlaceholder: t("core-packages.search.placeholders.driverAge"),
      searchButton: t("core-packages.search.button"),
    },
  };
};
